"use client";

import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { RefreshCw, Plus, ChevronUp } from "lucide-react";
import { toast } from "@/components/ui/use-toast";
import { WearableDeviceManager } from "@/components/wearables/WearableDeviceManager";
import { ViewSwitcher } from './ViewSwitcher';
import { MetricCard } from './MetricCard';
import { DataTabs } from './DataTabs';
import { WearableDataPoint, ViewType } from './types';
import { defaultMetrics } from './constants';
import { apiClient } from '@/lib/api-client';

export function WearableDataDisplay(): JSX.Element {
  const [wearableData, setWearableData] = useState<WearableDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [metrics, setMetrics] = useState(defaultMetrics);
  const [viewType, setViewType] = useState<any>('daily');
  const [draggedItem, setDraggedItem] = useState<any>(null);
  const [draggedOverItem, setDraggedOverItem] = useState<any>(null);
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [showDeviceManager, setShowDeviceManager] = useState(false);

  const fetchWearableData = async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.getWearableData();
      setWearableData(response.data as any);
      if (response?.data?.length > 0 && !activeProvider) {
        setActiveProvider((response.data as any)[0].provider);
      }
    } catch (error) {
      console.error('Error fetching wearable data:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to fetch wearable data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsLoading(true);
      const result = await apiClient.syncWearables();
      
      toast({
        title: "Success",
        description: "Successfully synced wearable data",
      });

      await fetchWearableData();
    } catch (error) {
      console.error('Error syncing data:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to sync data",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchWearableData();
  }, []);

  const handleConnect = () => {
    fetchWearableData();
  };

  const handleDragStart = (id: string) => {
    setDraggedItem(id);
  };

  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDraggedOverItem(id);
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    if (!draggedItem || draggedItem === targetId) return;

    const items = [...metrics];
    const draggedIndex = items.findIndex(item => item.id === draggedItem);
    const targetIndex = items.findIndex(item => item.id === targetId);

    const updatedItems = Array.isArray(items) ? items.map(item => {
      if (item.id === draggedItem) {
        return { ...item, order: targetIndex };
      }
      if (item.id === targetId) {
        return { ...item, order: draggedIndex };
      }
      return item;
    }) : [];

    updatedItems.sort((a, b) => a.order - b.order);
    setMetrics(updatedItems);
    setDraggedItem(null);
    setDraggedOverItem(null);
  };

  const getLayoutClasses = () => {
    switch (viewType) {
      case 'grid-2':
        return 'grid grid-cols-2 gap-4';
      case 'grid-3':
        return 'grid grid-cols-3 gap-3';
      default:
        return 'flex flex-col gap-2';
    }
  };

  const renderMetrics = (data: WearableDataPoint['data'], category: string) => {
    const size = viewType === 'list' ? 'large' : 'default';
    const hideLabel = viewType === 'grid-3';
    const filteredMetrics = category === 'overview' 
      ? metrics 
      : metrics.filter(m => m.category === category);

    return (
      <div className={`${getLayoutClasses()} animate-in fade-in-50 duration-500`}>
        {filteredMetrics?.map((metric) => (
          <div
            key={metric.id}
            draggable
            onDragStart={() => handleDragStart(metric.id)}
            onDragOver={(e) => handleDragOver(e, metric.id)}
            onDrop={(e) => handleDrop(e, metric.id)}
            className={`
              ${draggedOverItem === metric.id ? 'border-2 border-primary' : ''}
              ${draggedItem === metric.id ? 'opacity-50' : ''}
              transition-all duration-300
            `}
          >
            <MetricCard
              icon={metric.icon}
              label={metric.label}
              value={metric.value(data)}
              unit={metric.unit}
              size={size}
              hideLabel={hideLabel}
            />
          </div>
        ))}
      </div>
    );
  };

  const providers = Array.from(new Set(Array.isArray(wearableData) ? wearableData.map(d => d.provider) : []));

  if (!wearableData || wearableData.length === 0) {
    return (
      <Card className="animate-in fade-in zoom-in duration-500">
        <CardHeader>
          <CardTitle>Wearable Data</CardTitle>
          <CardDescription>No wearable data available.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={() => setShowDeviceManager(true)}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            Connect a Wearable Device
          </Button>
          {showDeviceManager && (
            <div className="mt-6 animate-in slide-in-from-top duration-500">
              <WearableDeviceManager />
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="animate-in fade-in zoom-in duration-500">
      <CardHeader className="flex flex-row items-center justify-between transition-all duration-500">
        <div>
          <CardTitle>Wearable Data</CardTitle>
          <CardDescription>View your synced wearable data</CardDescription>
        </div>
        <div className="flex items-center gap-2">
          <ViewSwitcher viewType={viewType} setViewType={setViewType} />
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowDeviceManager(!showDeviceManager)}
          >
            {showDeviceManager ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="outline"
            size="icon"
            onClick={handleSync}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="transition-all duration-500">
        {showDeviceManager && (
          <div className="mb-6 animate-in slide-in-from-top duration-500">
            <WearableDeviceManager />
          </div>
        )}
        {isLoading ? (
          <div className="flex items-center justify-center py-8 animate-pulse">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className="animate-in fade-in-50 duration-500">
            <Tabs
              value={activeProvider || undefined}
              onValueChange={(value: string) => setActiveProvider(value)}
            >
              <TabsList className="mb-4">
                {providers?.map((provider) => (
                  <TabsTrigger key={provider} value={provider} className="capitalize">
                    {provider.replace('-', ' ')}
                  </TabsTrigger>
                ))}
              </TabsList>
              {providers?.map((provider) => (
                <TabsContent key={provider} value={provider}>
                  {Array.isArray(wearableData) && Array.isArray(wearableData
                    .filter(d => d.provider === provider)
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())) && wearableData
                    .filter(d => d.provider === provider)
                    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                    .map((data) => (
                      <div key={data.id} className="mb-6">
                        <h3 className="text-sm font-medium mb-3 text-muted-foreground">
                          Last Updated: {new Date(data?.data?.lastUpdated).toLocaleString()}
                        </h3>
                        <DataTabs data={data.data} renderMetrics={renderMetrics} />
                      </div>
                    ))}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        )}
      </CardContent>
    </Card>
  );
}